import React, { useEffect, useRef, useState } from 'react'
import { Button } from 'react-bootstrap'

import useTranslation from '@/Hooks/UseTranslation'
import {
  confirmMsProjectImport,
  confirmExcelProjectImport,
  MsProjectImportResult,
  MsProjectPreview,
  MsProjectPreviewValidationError,
  MsProjectTaskCorrection,
  previewMsProjectImport,
  previewExcelProjectImport,
} from '@/Services/MsProjectImportService'
import Modal from '@/Components/Modal/Modal'
import MsProjectImportStepOne from './MsProjectImportStepOne'
import MsProjectImportStepTwo, {
  PreviewTab,
  ProgrammingRow,
  ValidationErrorGroup,
} from './MsProjectImportStepTwo'
import MsProjectImportStepThree from './MsProjectImportStepThree'

import './MsProjectImporter.scss'

type Props = {
  show: boolean
  onHide: () => void
  onImported?: () => void
  onOpenProject?: (projectId: string) => void
}

const MAX_FILE_SIZE = 100 * 1024 * 1024

type ImportStep = 'file' | 'review' | 'create'

const importSteps: ImportStep[] = [ 'file', 'review', 'create' ]

const getPreviewTabs = (preview: MsProjectPreview | null): PreviewTab[] => {
  if (!preview) return [ 'project', 'programming' ]
  if (preview.sourceType === 'gantt') return [ 'project', 'programming', ...(preview.colorCodes?.length ? [ 'colorCodes' as PreviewTab ] : []) ]
  if (preview.sourceType === 'pull_planning') return [ 'project', 'programming' ]
  return [ 'project', 'programming', ...(preview.colorCodes?.length ? [ 'colorCodes' as PreviewTab ] : []), ...(preview.relations.length ? [ 'relations' as PreviewTab ] : []), ...(preview.restrictions.length ? [ 'restrictions' as PreviewTab ] : []), ...(preview.costs.length ? [ 'costs' as PreviewTab ] : []) ]
}

const getValidationErrorTab = (errorGroup: ValidationErrorGroup): PreviewTab => {
  if (errorGroup.scope === 'task') return 'programming'
  if (errorGroup.scope === 'color_code') return 'colorCodes'

  return 'project'
}

const programmingIdCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
})

const normalizeProgrammingId = (value: string) => value.replace(/,/g, '.').replace(/\s/g, '')
const parentProgrammingId = (id: string) => id.includes('.') ? id.split('.').slice(0, -1).join('.') : null

const rebuildExcelHierarchy = (preview: MsProjectPreview): MsProjectPreview => {
  const groupsById = new Map(preview.groups.map(group => [ group.id, group ]))
  const tasksById = new Map(preview.tasks.map(task => [ task.id, task ]))

  const groups = preview.groups.map(group => {
    const parentId = parentProgrammingId(group.id)
    const parent = parentId ? groupsById.get(parentId) : undefined
    return { ...group, outlineLevel: group.id ? group.id.split('.').length : 0, parentId, parentUid: parent?.uid || null, parentName: parent?.name || null }
  })
  const rebuiltGroupsById = new Map(groups.map(group => [ group.id, group ]))
  const tasks = preview.tasks.map(task => {
    const parentId = parentProgrammingId(task.id)
    const parent = task.type === 'trabajo'
      ? (parentId ? tasksById.get(parentId) : undefined)
      : (parentId ? rebuiltGroupsById.get(parentId) : undefined)
    return { ...task, parentId, parentUid: parent?.uid || null, parentName: parent?.name || null }
  })

  return { ...preview, groups, tasks }
}

const groupValidationErrors = (errors: MsProjectPreviewValidationError[]): ValidationErrorGroup[] => {

  const groups: ValidationErrorGroup[] = []
  const groupsByKey: Record<string, ValidationErrorGroup> = {}

  errors.forEach((error, index) => {
    const key = error.scope === 'project'
      ? 'project'
      : `task-${error.taskUid || error.taskId || index}`
    let group = groupsByKey[key]

    if (!group) {
      group = {
        key,
        scope: error.scope,
        taskUid: error.taskUid,
        taskId: error.taskId,
        taskName: error.taskName,
        errors: [],
      }
      groupsByKey[key] = group
      groups.push(group)
    }

    group.errors.push(error)
  })

  return groups
}

const errorTranslationKeys: Record<string, string> = {
  ms_project_import_file_missing: 'MS_PROJECT_IMPORTER:FileMissing',
  ms_project_import_invalid_extension: 'MS_PROJECT_IMPORTER:InvalidExtension',
  ms_project_import_file_too_large: 'MS_PROJECT_IMPORTER:FileTooLarge',
  ms_project_import_invalid_file: 'MS_PROJECT_IMPORTER:InvalidFile',
  ms_project_import_empty_tasks: 'MS_PROJECT_IMPORTER:EmptyTasks',
  ms_project_import_parse_failed: 'MS_PROJECT_IMPORTER:ParseFailed',
  ms_project_import_validation_failed: 'MS_PROJECT_IMPORTER:ValidationFailed',
  ms_project_import_invalid_project_dates: 'MS_PROJECT_IMPORTER:InvalidProjectDates',
  ms_project_import_invalid_corrections: 'MS_PROJECT_IMPORTER:InvalidCorrections',
  excel_import_invalid_extension: 'MS_PROJECT_IMPORTER:InvalidExtension',
  excel_import_invalid_file: 'MS_PROJECT_IMPORTER:InvalidFile',
  excel_import_unknown_format: 'TASK_IMPORTER:FormatUnknown',
  excel_import_invalid_sheet: 'MS_PROJECT_IMPORTER:InvalidSheet',
  excel_import_empty_tasks: 'MS_PROJECT_IMPORTER:EmptyTasks',
  excel_import_validation_failed: 'MS_PROJECT_IMPORTER:ValidationFailed',
}

const MsProjectImporter = ({ show, onHide, onImported, onOpenProject }: Props) => {

  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const [ step, setStep ] = useState<ImportStep>('file')
  const [ selectedFile, setSelectedFile ] = useState<File | null>(null)
  const [ preview, setPreview ] = useState<MsProjectPreview | null>(null)
  const [ importId, setImportId ] = useState<string | null>(null)
  const [ activeTab, setActiveTab ] = useState<PreviewTab>('project')
  const [ loading, setLoading ] = useState(false)
  const [ importing, setImporting ] = useState(false)
  const [ errorKey, setErrorKey ] = useState<string | null>(null)
  const [ importResult, setImportResult ] = useState<MsProjectImportResult | null>(null)
  const [ taskCorrections, setTaskCorrections ] = useState<Record<string, MsProjectTaskCorrection>>({})
  const [ correctionTargetUid, setCorrectionTargetUid ] = useState<string | null>(null)
  const [ highlightedProgrammingUid, setHighlightedProgrammingUid ] = useState<string | null>(null)
  const [ colorCodeTargetId, setColorCodeTargetId ] = useState<string | null>(null)
  const [ highlightedColorCodeId, setHighlightedColorCodeId ] = useState<string | null>(null)

  useEffect(() => {
    if (activeTab !== 'programming' || !correctionTargetUid) return

    const targetRow = Array.from(
      contentRef.current?.querySelectorAll<HTMLElement>('[data-programming-uid]') || [],
    ).find(row => row.dataset.programmingUid === correctionTargetUid)

    if (!targetRow) return

    targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setHighlightedProgrammingUid(correctionTargetUid)

    const highlightTimeout = window.setTimeout(() => {
      setHighlightedProgrammingUid(current => current === correctionTargetUid ? null : current)
    }, 5000)

    return () => window.clearTimeout(highlightTimeout)
  }, [ activeTab, correctionTargetUid ])

  useEffect(() => {
    if (activeTab !== 'colorCodes' || !colorCodeTargetId) return

    const targetRow = Array.from(
      contentRef.current?.querySelectorAll<HTMLElement>('[data-color-code-id]') || [],
    ).find(row => row.dataset.colorCodeId === colorCodeTargetId)

    if (!targetRow) return

    targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setHighlightedColorCodeId(colorCodeTargetId)

    const timeout = window.setTimeout(() => {
      setHighlightedColorCodeId(current => current === colorCodeTargetId ? null : current)
    }, 5000)

    return () => window.clearTimeout(timeout)
  }, [ activeTab, colorCodeTargetId ])

  const reset = () => {

    setStep('file')
    setSelectedFile(null)
    setPreview(null)
    setImportId(null)
    setActiveTab('project')
    setLoading(false)
    setImporting(false)
    setErrorKey(null)
    setImportResult(null)
    setTaskCorrections({})
    setCorrectionTargetUid(null)
    setHighlightedProgrammingUid(null)
    setColorCodeTargetId(null)
    setHighlightedColorCodeId(null)
  }

  const handleHide = () => {

    if (loading || importing) return
    if (preview && !importResult && !window.confirm(t('MS_PROJECT_IMPORTER:DiscardConfirmation'))) return

    reset()
    onHide()
  }

  const getErrorKey = (error: any) => {

    const backendKey = error?.response?.data?.error || error?.message

    return errorTranslationKeys[backendKey] || 'MS_PROJECT_IMPORTER:UnexpectedError'
  }

  const validateFile = (file: File) => {

    if (!/\.(mpp|xlsx|xls)$/i.test(file.name)) {

      setErrorKey('MS_PROJECT_IMPORTER:InvalidExtension')
      return false
    }

    if (file.size > MAX_FILE_SIZE) {

      setErrorKey('MS_PROJECT_IMPORTER:FileTooLarge')
      return false
    }

    return true
  }

  const handleSelectedFile = async (file?: File) => {

    if (!file || !validateFile(file)) return

    setSelectedFile(file)
    setPreview(null)
    setErrorKey(null)
    setImportResult(null)
    setStep('file')
    setLoading(true)

    try {

      const isMsProject = file.name.toLowerCase().endsWith('.mpp')
      const result = isMsProject ? await previewMsProjectImport(file) : await previewExcelProjectImport(file)
      setPreview(result.preview)
      setImportId(isMsProject ? (result as any).importId : 'excel-preview')
      setTaskCorrections({})
      setActiveTab('project')

      // Fast-path: the backend already has a completed import for this exact
      // file, so there is nothing to review. Confirm immediately — the backend
      // will return the existing project instead of creating a duplicate.
      if (isMsProject && (result as any).deduplicated) {
        setStep('create')
        const confirmed = await confirmMsProjectImport((result as any).importId, [])
        setImportResult(confirmed)
        onImported?.()
        return
      }

      setStep('review')
    } catch (error) {

      setErrorKey(getErrorKey(error))
      setStep('file')
    } finally {

      setLoading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {

    handleSelectedFile(e.target.files?.[0])
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {

    e.preventDefault()
    e.stopPropagation()
    handleSelectedFile(e.dataTransfer.files?.[0])
  }

  const handleTabChange = (tab: PreviewTab) => {

    setCorrectionTargetUid(null)
    setHighlightedProgrammingUid(null)
    setColorCodeTargetId(null)
    setHighlightedColorCodeId(null)
    setActiveTab(tab)

    if (contentRef.current) contentRef.current.scrollTop = 0
  }

  const handleCorrectValidationError = (errorGroup: ValidationErrorGroup) => {
    const correctionTab = getValidationErrorTab(errorGroup)

    setCorrectionTargetUid(correctionTab === 'programming' ? errorGroup.taskUid || null : null)
    setColorCodeTargetId(correctionTab === 'colorCodes' ? errorGroup.errors[0]?.colorCodeId || null : null)
    setActiveTab(correctionTab)

    if (contentRef.current) contentRef.current.scrollTop = 0
  }

  const handleGoToProgrammingRow = (uid: string) => {
    setCorrectionTargetUid(uid)
    setActiveTab('programming')

    if (contentRef.current) contentRef.current.scrollTop = 0
  }

  const handleGoToColorCode = (id: string) => {
    setColorCodeTargetId(id)
    setActiveTab('colorCodes')

    if (contentRef.current) contentRef.current.scrollTop = 0
  }

  const handleReviewBack = () => {
    const previewTabs = getPreviewTabs(preview)
    const tabIndex = previewTabs.indexOf(activeTab)

    if (tabIndex > 0) {
      handleTabChange(previewTabs[tabIndex - 1])
      return
    }

    reset()
  }

  const handleGoToConfirmation = () => {
    if (!preview?.validation.isValid) return

    setStep('create')
    if (contentRef.current) contentRef.current.scrollTop = 0
  }

  const handleReviewContinue = () => {
    const previewTabs = getPreviewTabs(preview)
    const tabIndex = previewTabs.indexOf(activeTab)

    if (tabIndex < previewTabs.length - 1) {
      handleTabChange(previewTabs[tabIndex + 1])
      return
    }

    handleGoToConfirmation()
  }

  const handleBackToReview = () => {
    const previewTabs = getPreviewTabs(preview)

    setStep('review')
    setActiveTab(previewTabs[previewTabs.length - 1])
    if (contentRef.current) contentRef.current.scrollTop = 0
  }

  const handleOpenProject = () => {

    const projectId = importResult?.project?._id
    if (!projectId || !onOpenProject) return

    onOpenProject(projectId)
    handleHide()
  }

  const handleConfirmImport = async () => {

    if (!importId || !preview?.validation.isValid) return

    setImporting(true)
    setErrorKey(null)

    try {

      const result = preview.sourceType === 'gantt' || preview.sourceType === 'pull_planning'
        ? await confirmExcelProjectImport(preview)
        : await confirmMsProjectImport(importId, Object.values(taskCorrections))
      setImportResult(result)
      onImported?.()
    } catch (error) {

      setErrorKey(getErrorKey(error))
    } finally {

      setImporting(false)
    }
  }

  const validationErrorGroups = groupValidationErrors(preview?.validation.errors || [])
  const errorTaskUids = new Set(validationErrorGroups.flatMap(group => group.taskUid ? [group.taskUid] : []))
  const previewTabs = getPreviewTabs(preview)
  const activeReviewTabIndex = previewTabs.indexOf(activeTab)
  const isLastReviewTab = activeReviewTabIndex === previewTabs.length - 1
  const hasBlockingValidationErrors = !preview?.validation.isValid
    && (activeTab === 'programming' || isLastReviewTab)
  const activeStepIndex = importSteps.indexOf(step)

  const updateTaskDate = (uid: string, field: 'start' | 'finish', value: string) => {
    setTaskCorrections(current => ({
      ...current,
      [uid]: { ...current[uid], uid, [field]: value || null },
    }))
    setPreview(current => {
      if (!current) return current

      const tasks = current.tasks.map(task => task.uid === uid ? { ...task, [field]: value || null } : task)
      const missingCode = field === 'start' ? 'task_start_missing' : 'task_finish_missing'
      const errors = current.validation.errors.filter(error => !(error.taskUid === uid && error.code === missingCode && value))

      return validateExcelPreview({ ...current, tasks, validation: { ...current.validation, isValid: errors.length === 0, errors } })
    })
  }

  const updateTaskName = (uid: string, name: string) => {
    setTaskCorrections(current => ({
      ...current,
      [uid]: { ...current[uid], uid, name },
    }))
    setPreview(current => current ? validateExcelPreview({
      ...current,
      tasks: current.tasks.map(task => task.uid === uid ? { ...task, name } : task),
    }) : current)
  }

  const updateTaskId = (uid: string, value: string) => {
    setPreview(current => {
      if (!current || (current.sourceType !== 'gantt' && current.sourceType !== 'pull_planning')) return current

      const currentItem = [ ...current.groups, ...current.tasks ].find(item => item.uid === uid)
      if (!currentItem) return current

      const nextId = normalizeProgrammingId(value)
      const descendantPrefix = `${currentItem.id}.`
      const replaceId = (item: { uid: string; id: string }) => item.uid === uid
        ? nextId
        : item.id.startsWith(descendantPrefix)
          ? `${nextId}${item.id.slice(currentItem.id.length)}`
          : item.id
      const rebuilt = rebuildExcelHierarchy({
        ...current,
        groups: current.groups.map(group => ({ ...group, id: replaceId(group) })),
        tasks: current.tasks.map(task => ({ ...task, id: replaceId(task) })),
      })

      return validateExcelPreview(rebuilt)
    })
  }

  const validateExcelPreview = (current: MsProjectPreview): MsProjectPreview => {
    if (current.sourceType !== 'gantt' && current.sourceType !== 'pull_planning') return current

    const recalculatedCodes = new Set([
      'project_name_missing',
      'project_dates_invalid',
      'task_outside_project',
      'task_start_invalid',
      'task_finish_invalid',
      'task_finish_before_start',
      'task_invalid',
      'task_id_missing',
      'task_id_invalid',
      'task_id_duplicate',
      'task_parent_missing',
      'task_id_out_of_order',
      'task_id_not_consecutive',
      'color_code_invalid',
      'color_code_duplicate',
    ])
    const errors = current.validation.errors.filter(error => !recalculatedCodes.has(error.code))

    if (!current.project.name.trim()) {
      errors.push({ code: 'project_name_missing', scope: 'project', message: t('MS_PROJECT_IMPORTER:ProjectNameRequired') })
    }
    if (!current.project.startsAt || !current.project.endsAt || current.project.endsAt < current.project.startsAt) {
      errors.push({ code: 'project_dates_invalid', scope: 'project', message: t('MS_PROJECT_IMPORTER:InvalidProjectDates') })
    }
    const allItems = [ ...current.groups, ...current.tasks ]
    const ids = new Map<string, number>()
    allItems.forEach(item => ids.set(item.id, (ids.get(item.id) || 0) + 1))
    allItems.forEach(item => {
      if (!item.id) {
        errors.push({ code: 'task_id_missing', scope: 'task', taskUid: item.uid, taskId: item.id, taskName: item.name, message: t('MS_PROJECT_IMPORTER:TaskIdRequired') })
      } else if (!/^[1-9]\d*(?:\.[1-9]\d*)*$/.test(item.id)) {
        errors.push({ code: 'task_id_invalid', scope: 'task', taskUid: item.uid, taskId: item.id, taskName: item.name, message: t('MS_PROJECT_IMPORTER:TaskIdInvalid') })
      }
      if (item.id && (ids.get(item.id) || 0) > 1) {
        errors.push({ code: 'task_id_duplicate', scope: 'task', taskUid: item.uid, taskId: item.id, taskName: item.name, message: t('MS_PROJECT_IMPORTER:TaskIdDuplicate') })
      }
      const parentId = parentProgrammingId(item.id)
      if (parentId && !item.parentUid) {
        errors.push({ code: 'task_parent_missing', scope: 'task', taskUid: item.uid, taskId: item.id, taskName: item.name, message: t('MS_PROJECT_IMPORTER:TaskParentMissing') })
      }
    })
    if (current.sourceType === 'pull_planning') {
      const sourceItems = allItems.slice().sort((left, right) => (left.importSource.sourceRow || 0) - (right.importSource.sourceRow || 0))
      sourceItems.forEach((item, index) => {
        if (!index || !/^[1-9]\d*(?:\.[1-9]\d*)*$/.test(item.id)) return
        const previous = sourceItems[index - 1]
        if (!/^[1-9]\d*(?:\.[1-9]\d*)*$/.test(previous.id)) return
        if (programmingIdCollator.compare(item.id, previous.id) < 0) {
          errors.push({ code: 'task_id_out_of_order', scope: 'task', taskUid: item.uid, taskId: item.id, taskName: item.name, message: t('MS_PROJECT_IMPORTER:TaskIdOutOfOrder') })
        }
        const previousParts = previous.id.split('.').map(Number)
        const parts = item.id.split('.').map(Number)
        const commonLength = Math.min(previousParts.length, parts.length)
        let consecutive = false
        for (let partIndex = 0; partIndex < commonLength; partIndex += 1) {
          if (previousParts[partIndex] !== parts[partIndex]) {
            consecutive = partIndex === commonLength - 1 && parts[partIndex] === previousParts[partIndex] + 1
            break
          }
        }
        if (!consecutive && parts.length === previousParts.length + 1) consecutive = parts[parts.length - 1] === 1
        if (!consecutive) errors.push({ code: 'task_id_not_consecutive', scope: 'task', taskUid: item.uid, taskId: item.id, taskName: item.name, message: t('MS_PROJECT_IMPORTER:TaskIdNotConsecutive') })
      })
    }
    current.tasks.forEach(task => {
      if (!task.name.trim()) {
        errors.push({ code: 'task_invalid', scope: 'task', taskUid: task.uid, taskId: task.id, taskName: task.name, message: t('MS_PROJECT_IMPORTER:TaskNameRequired') })
      }
      if (!task.start) {
        errors.push({ code: 'task_start_invalid', scope: 'task', taskUid: task.uid, taskId: task.id, taskName: task.name, message: t('MS_PROJECT_IMPORTER:TaskStartRequired') })
      }
      if (!task.finish) {
        errors.push({ code: 'task_finish_invalid', scope: 'task', taskUid: task.uid, taskId: task.id, taskName: task.name, message: t('MS_PROJECT_IMPORTER:TaskFinishRequired') })
      }
      if (task.start && task.finish && task.finish < task.start) {
        errors.push({ code: 'task_finish_before_start', scope: 'task', taskUid: task.uid, taskId: task.id, taskName: task.name, message: t('TASK_IMPORTER:EndDateIsBeforeStartDate') })
      }
      if ((current.project.startsAt && task.start && task.start < current.project.startsAt)
        || (current.project.endsAt && task.finish && task.finish > current.project.endsAt)) {
        errors.push({ code: 'task_outside_project', scope: 'task', taskUid: task.uid, taskId: task.id, taskName: task.name, message: t('MS_PROJECT_IMPORTER:TaskOutsideProject') })
      }
    })
    const names = new Set<string>()
    ;(current.colorCodes || []).forEach(code => {
      const normalized = code.name.trim().toLowerCase()
      if (!normalized || !/^#[0-9a-f]{6}$/i.test(code.color)) {
        errors.push({ code: 'color_code_invalid', scope: 'color_code', colorCodeId: code.id, message: t('MS_PROJECT_IMPORTER:InvalidColorCode') })
      } else if (names.has(normalized)) {
        errors.push({ code: 'color_code_duplicate', scope: 'color_code', colorCodeId: code.id, message: t('MS_PROJECT_IMPORTER:DuplicateColorCode') })
      }
      names.add(normalized)
    })

    return { ...current, validation: { ...current.validation, errors, isValid: errors.length === 0 } }
  }

  const updateProject = (field: 'name' | 'startsAt' | 'endsAt', value: string) => {
    setPreview(current => current ? validateExcelPreview({
      ...current,
      project: { ...current.project, [field]: value, ...(field === 'name' ? { shortName: value } : {}) },
    }) : current)
  }

  const updateColorCode = (id: string, field: 'name' | 'color', value: string) => {
    setPreview(current => current ? validateExcelPreview({
      ...current,
      colorCodes: (current.colorCodes || []).map(code => code.id === id ? { ...code, [field]: value } : code),
    }) : current)
  }

  const selectSheet = async (sheetName: string) => {
    if (!selectedFile || selectedFile.name.toLowerCase().endsWith('.mpp')) return

    setLoading(true)
    setErrorKey(null)
    try {
      const result = await previewExcelProjectImport(selectedFile, sheetName)
      setPreview(result.preview)
      setActiveTab('project')
    } catch (error) {
      setErrorKey(getErrorKey(error))
    } finally {
      setLoading(false)
    }
  }

  const excludeTask = (uid: string) => {
    setTaskCorrections(current => ({ ...current, [uid]: { uid, exclude: true } }))
    setPreview(current => {
      if (!current) return current

      const excluded = new Set([uid])
      let changed = true
      while (changed) {
        changed = false
        current.tasks.forEach(task => {
          if (task.parentUid && excluded.has(task.parentUid) && !excluded.has(task.uid)) {
            excluded.add(task.uid)
            changed = true
          }
        })
      }

      const tasks = current.tasks.filter(task => !excluded.has(task.uid))
      const errors = current.validation.errors.filter(error => !error.taskUid || !excluded.has(error.taskUid))
      const relations = current.relations.filter(item => !excluded.has(item.fromUid) && !excluded.has(item.toUid))
      const restrictions = current.restrictions.filter(item => !excluded.has(item.taskUid))
      const costs = current.costs.filter(item => !excluded.has(item.taskUid))

      return {
        ...current,
        tasks,
        relations,
        restrictions,
        costs,
        baselines: current.baselines?.map(baseline => ({ ...baseline, tasks: baseline.tasks.filter(item => !excluded.has(item.taskUid)) })),
        summary: {
          ...current.summary,
          taskCount: tasks.length,
          itemCount: tasks.filter(task => task.type === 'partida').length,
          workCount: tasks.filter(task => task.type === 'trabajo').length,
          relationCount: relations.length,
          restrictionCount: restrictions.length,
          costCount: costs.length,
          totalEstimatedCost: costs.reduce((sum, cost) => sum + cost.estimatedCost, 0),
        },
        validation: { isValid: errors.length === 0, errors },
      }
    })
  }
  const programmingRows: ProgrammingRow[] = preview ? [
    ...preview.groups.map((group) => ({
      uid: group.uid,
      id: group.id,
      name: group.name,
      type: 'group' as const,
      start: null,
      finish: null,
      percentComplete: null,
      colorCodeId: null,
      importSource: group.importSource,
    })),
    ...preview.tasks.map((task) => ({
      uid: task.uid,
      id: task.id,
      name: task.name,
      type: task.type,
      start: task.start,
      finish: task.finish,
      percentComplete: task.percentComplete,
      colorCodeId: task.colorCodeId || null,
      importSource: task.importSource,
    })),
  ].sort((first, second) => programmingIdCollator.compare(first.id, second.id)) : []

  const footer = step === 'review' ? (
    <>
      <Button variant="secondary" onClick={handleReviewBack} disabled={loading || importing}>
        {t('MS_PROJECT_IMPORTER:BackButton')}
      </Button>
      <Button
        variant="secondary"
        disabled={hasBlockingValidationErrors || loading || importing}
        onClick={handleReviewContinue}
      >
        {t('MS_PROJECT_IMPORTER:ContinueButton')}
      </Button>
      {!isLastReviewTab && (
        <Button
          variant="primary"
          disabled={!preview?.validation.isValid || loading || importing}
          onClick={handleGoToConfirmation}
        >
          {t('MS_PROJECT_IMPORTER:GoToConfirmationButton')}
        </Button>
      )}
    </>
  ) : step === 'create' && importResult ? (
    <>
      <Button variant="secondary" onClick={handleHide}>
        {t('MS_PROJECT_IMPORTER:CloseButton')}
      </Button>
      {onOpenProject && importResult.project?._id && (
        <Button variant="primary" onClick={handleOpenProject}>
          {t('MS_PROJECT_IMPORTER:OpenProjectButton')}
        </Button>
      )}
    </>
  ) : step === 'create' ? (
    <>
      <Button variant="secondary" onClick={handleBackToReview} disabled={importing}>
        {t('MS_PROJECT_IMPORTER:BackButton')}
      </Button>
      <Button variant="primary" onClick={handleConfirmImport} disabled={importing}>
        {importing ? t('MS_PROJECT_IMPORTER:ImportingButton') : t('MS_PROJECT_IMPORTER:CreateProjectButton')}
      </Button>
    </>
  ) : undefined

  return (
    <Modal
      show={show}
      title={t('MS_PROJECT_IMPORTER:Title')}
      onClose={handleHide}
      showCloseButton={!loading && !importing}
      backdrop="static"
      keyboard
      size="xl"
      className="ms-project-importer-modal"
      footer={footer}
    >
        <nav className="ms-project-importer-stepper" aria-label={t('MS_PROJECT_IMPORTER:StepsLabel')}>
          <ol>
            {importSteps.map((item, index) => {
              const completed = index < activeStepIndex || (Boolean(importResult) && item === 'create')

              return (
                <li
                  key={item}
                  className={`${index === activeStepIndex ? 'active' : ''} ${completed ? 'completed' : ''}`}
                  aria-current={index === activeStepIndex ? 'step' : undefined}
                >
                  <span className="ms-project-importer-step-number" aria-hidden="true">
                    {completed ? '✓' : index + 1}
                  </span>
                  <span className="ms-project-importer-step-label">{t(`MS_PROJECT_IMPORTER:Step:${item}`)}</span>
                </li>
              )
            })}
          </ol>
        </nav>
        {step === 'file' && (
          <div ref={contentRef} className="ms-project-importer-content custom-scrollbar">
            {errorKey && <div className="alert alert-danger" role="alert">{t(errorKey)}</div>}
            <MsProjectImportStepOne
              inputRef={inputRef}
              loading={loading}
              selectedFile={selectedFile}
              onDrop={handleDrop}
              onFileChange={handleFileChange}
            />
          </div>
        )}

        {step === 'review' && preview && (
          <MsProjectImportStepTwo
            activeTab={activeTab}
            contentRef={contentRef}
            errorKey={errorKey}
            errorTaskUids={errorTaskUids}
            highlightedProgrammingUid={highlightedProgrammingUid}
            highlightedColorCodeId={highlightedColorCodeId}
            preview={preview}
            programmingRows={programmingRows}
            validationErrorGroups={validationErrorGroups}
            onCorrectValidationError={handleCorrectValidationError}
            onExcludeTask={excludeTask}
            onGoToProgrammingRow={handleGoToProgrammingRow}
            onGoToColorCode={handleGoToColorCode}
            onTabChange={handleTabChange}
            onUpdateTaskDate={updateTaskDate}
            onUpdateTaskId={updateTaskId}
            onUpdateTaskName={updateTaskName}
            onUpdateColorCode={updateColorCode}
            onUpdateProject={updateProject}
            onSelectSheet={selectSheet}
          />
        )}

        {step === 'create' && (
          <div ref={contentRef} className="ms-project-importer-content custom-scrollbar">
            {errorKey && <div className="alert alert-danger" role="alert">{t(errorKey)}</div>}
            <MsProjectImportStepThree
              importing={importing}
              preview={preview}
              result={importResult}
            />
          </div>
        )}
    </Modal>
  )
}

export default MsProjectImporter
